// Lightweight date utilities used across components
export const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Convert a Date to YYYY-MM-DD (local date, no time)
 */
export function toDateString(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Step a YYYY-MM-DD date by n days (negative for earlier). Local-midnight
 * arithmetic, so a clock change can't shift the day.
 */
export function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T00:00');
  d.setDate(d.getDate() + n);
  return toDateString(d);
}

/**
 * Whole days from one YYYY-MM-DD date to another (negative if `to` is
 * earlier). Rounded, so a clock change between them can't make it 6.96.
 */
export function daysBetween(from: string, to: string): number {
  return Math.round((new Date(to + 'T00:00').getTime() - new Date(from + 'T00:00').getTime()) / 86_400_000);
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format a date for display as "dd MMM yyyy" (e.g. "05 May 2026"). The
 * canonical display format used across the schedule + attendance pages.
 * Accepts either a Date or a YYYY-MM-DD string. Returns an empty string
 * if the input is null/undefined or unparseable.
 */
export function formatDisplayDate(input: Date | string | null | undefined): string {
  if (input == null) return '';
  let d: Date;
  if (typeof input === 'string') {
    // Parse YYYY-MM-DD as local midnight to avoid timezone drift.
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  } else {
    d = input;
  }
  if (isNaN(d.getTime())) return '';
  return `${pad(d.getDate())} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Return the Monday (local) for the week containing date d.
 * If d is Sunday, return the previous Monday.
 */
export function getMonday(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate()); // strip time
  const day = copy.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day);
  copy.setDate(copy.getDate() + diff);
  return copy;
}

/**
 * Return the Friday (local) for the week containing date d.
 * If d is Sunday, return the Friday of the previous week.
 */
export function getFriday(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate()); // strip time
  const day = copy.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -2 : 5 - day);
  copy.setDate(copy.getDate() + diff);
  return copy;
}

export default {
  pad,
  toDateString,
  getMonday,
  getFriday,
  formatDisplayDate,
};
